import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageHero from "@/components/PageHero";
import FinalCTA from "@/components/FinalCTA";
import UniversityCard from "@/components/UniversityCard";
import { FadeUp } from "@/components/Reveal";
import { SEO } from "@/lib/seo";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search } from "lucide-react";
import { useSavedUniversities } from "@/lib/useSaved";

export default function Universities() {
  const { saved, toggle } = useSavedUniversities();
  const [universities, setUniversities] = useState([]);
  const [meta, setMeta] = useState({ cities: [], states: [], groups: [], fields: [] });
  const [filters, setFilters] = useState({ q: "", city: "", state: "", group: "", field: "" });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/universities/meta/filters").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => v && (params[k] = v));
    api.get("/universities", { params })
      .then((r) => setUniversities(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filters]);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v === "__all__" ? "" : v }));
  const go8 = universities.filter((u) => u.group === "Group of Eight");
  const others = universities.filter((u) => u.group !== "Group of Eight");

  return (
    <div data-testid="universities-page">
      <SEO title="Australian Universities Directory | Vitaliah" description="Browse Australian universities for international students — filter by city, state, field of study and university group, including the Group of Eight." />
      <Navbar />
      <PageHero
        overline="Directory"
        title="Australian universities, clearly organised."
        subtitle="Filter by city, state, field of study or university group — and understand which universities may suit your goals."
      />

      <section className="py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <div data-testid="university-filters" className="grid grid-cols-2 lg:grid-cols-5 gap-4 bg-white border border-border p-6">
              <div className="col-span-2 lg:col-span-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-navy/40" />
                <Input data-testid="filter-search" value={filters.q} onChange={(e) => set("q", e.target.value)} placeholder="Search universities" className="rounded-none pl-10 border-input focus-visible:ring-gold" />
              </div>
              {[
                { k: "city", label: "City", options: meta.cities },
                { k: "state", label: "State", options: meta.states },
                { k: "field", label: "Field of study", options: meta.fields },
                { k: "group", label: "University group", options: meta.groups },
              ].map((f) => (
                <Select key={f.k} value={filters[f.k] || "__all__"} onValueChange={(v) => set(f.k, v)}>
                  <SelectTrigger data-testid={`filter-${f.k}`} className="rounded-none border-input focus:ring-gold">
                    <SelectValue placeholder={f.label} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All {f.label.toLowerCase()}s</SelectItem>
                    {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              ))}
            </div>
          </FadeUp>

          {go8.length > 0 && (
            <div className="mt-16">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Featured — Group of Eight</p>
              <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="go8-directory-grid">
                {go8.map((u, i) => <UniversityCard key={u.id} u={u} index={i} saved={saved.has(u.slug)} onToggleSave={toggle} />)}
              </div>
            </div>
          )}
          {others.length > 0 && (
            <div className="mt-16">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-navy/50">More universities</p>
              <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {others.map((u, i) => <UniversityCard key={u.id} u={u} index={i} saved={saved.has(u.slug)} onToggleSave={toggle} />)}
              </div>
            </div>
          )}
          {!loading && universities.length === 0 && (
            <p data-testid="universities-empty" className="mt-20 text-center text-navy/50">No universities match your filters yet. Try broadening your search.</p>
          )}
        </div>
      </section>
      <FinalCTA />
      <Footer />
    </div>
  );
}
